import { Box, Stack, Typography, useTheme } from '@mui/material'
// No `ChevronRightIcon`: the collapsed chevron is `ChevronDownIcon` rotated -90deg, so
// the two states are one glyph that travels between them rather than two that swap.
import { ChevronDownIcon } from 'lucide-react'

/**
 * A LOW-EMPHASIS disclosure trigger: a chevron and a word, no border, no fill.
 *
 * ## Why this is not a `Button`
 *
 * `@repo/ui`'s `Button` is the right default for an ACTION, and this is not one — it
 * reveals or hides something already on the page. Rendered as `variant="secondary"` it
 * came out as a 40px bordered control sitting beside the card's real controls, at the same
 * visual weight as the period select, which made "look at the chart" compete with the
 * choice that changes every number on the card. `variant="text"` is closer but still
 * carries the button chassis (40px height, horizontal padding, a grey hover pill) and its
 * ink is `text.primary`, so it reads as a control rather than as a link into detail.
 *
 * The house treatment for this job already existed — the plan-comparison matrix's
 * expand/collapse-all — and this is that treatment extracted on its second use (§36),
 * not a new invention. Both call sites now render the same element.
 *
 * ## It is still a real `<button>`
 *
 * The original was a `Stack` with an `onClick`, which is not reachable by keyboard, has no
 * role, and announces nothing. Low emphasis is a VISUAL decision; it is not a licence to
 * drop the semantics. So this renders `component="button"` with `aria-expanded` and an
 * optional `aria-controls`, and strips the UA button chrome rather than avoiding the
 * element that carries the behaviour.
 *
 * ## The chevron convention: DOWN means expanded
 *
 * Down = expanded, rotated -90deg (i.e. pointing right) = collapsed. That is the
 * convention the matrix's section headers and its master control already use, and the one
 * every accordion in this app follows.
 *
 * ⚠️ It is the OPPOSITE of the rotate-180 "point up when open" convention the ROI hero
 * used before adopting this component. Both are defensible read alone; two of them in one
 * product is not, because a reader who learns one is actively misled by the other. The
 * accordion convention wins on prevalence.
 *
 * ## ITS WIDTH DOES NOT DEPEND ON ITS STATE, AND THAT IS LOAD-BEARING
 *
 * A disclosure's two labels are almost never the same length — "Collapse chart" measures
 * 107.8px and "Expand chart" 100.6px — so the naive `expanded ? a : b` gives this button a
 * width that changes every time it is pressed. That is not a cosmetic detail about the
 * button; it is a 7px shove delivered to whatever shares its row.
 *
 * On the ROI hero it moved the KPI figures SIDEWAYS on a control whose entire job is to
 * change the page's HEIGHT. The button sits in a `space-between` row opposite the figure
 * group, and that group is `flex: 1 1 520px` — grow 1, so it absorbs every pixel the
 * controls give up and redistributes it through its own 1:2 split. Measured: pressing this
 * moved "Value delivered" 2.39px and "Spend with Vera" 4.78px. Small, and precisely the
 * kind of movement a reader notices without being able to name.
 *
 * So both labels are handed in together (see `label`) and the button reserves the width of
 * the WIDER one. The reservation is a one-cell grid holding every label stacked, sized by
 * the widest, rather than a `minWidth` in pixels — a hardcoded width is measured against
 * one font at one zoom in one language and is wrong in all the others, whereas the grid
 * re-measures whatever it is actually given.
 *
 * The slack sits at the TRAILING edge, so the chevron and the label's first character stay
 * put across a toggle. Those are the two things the eye tracks — the chevron especially,
 * since it is mid-rotation at the time — and reserving on the leading edge instead would
 * make the rotating glyph also jump sideways, which is a worse artefact than a few pixels
 * of trailing whitespace.
 */
export interface DisclosureTextButtonProps {
  /** True when the region this controls is showing. Drives the chevron and `aria-expanded`. */
  expanded: boolean
  onToggle: () => void
  /**
   * The label, which must name the ACTION rather than the content.
   *
   * "Collapse chart" / "Expand chart", not "Value delivered over time": with the region
   * already open, a trigger labelled with what is visible below it does not read as a
   * control at all. The chevron and `aria-expanded` carry the state, so the words are free
   * to carry the verb.
   *
   * ⚠️ PASS THE PAIR, NOT A TERNARY. Both real call sites swap between two labels of
   * different lengths ("Collapse chart"/"Expand chart", "Collapse all"/"Expand all"), and a
   * caller resolving that itself hands this component a width that changes with its own
   * state — see the docblock's width note above for what that then moves. Handing over both
   * strings is what lets the button reserve the wider one. A plain `string` remains valid
   * for the rare trigger whose label genuinely does not change.
   */
  label: string | { expanded: string; collapsed: string }
  /** Id of the region this discloses, for `aria-controls`. */
  controls?: string
  /**
   * `inverse` for a trigger sitting on a fixed DARK surface whatever the theme mode — the
   * Vera-space banner (VRG-677) is zinc-800 in light and dark alike, so `text.secondary`
   * there is dark grey on dark grey. Light ink instead, same hierarchy (muted at rest,
   * full on hover/focus).
   */
  tone?: 'default' | 'inverse'
}

/**
 * Motion lifted from `@repo/ui`'s `useAnimatedHeight` rather than picked, so a chevron
 * turning here and a container resizing elsewhere travel on the same curve at the same
 * speed. Decelerating, no overshoot: overshoot on a disclosure reads as instability.
 */
const MOTION_MS = 260
const EASING = 'cubic-bezier(0.32, 0.72, 0, 1)'

export const DisclosureTextButton = ({
  expanded,
  onToggle,
  label,
  controls,
  tone = 'default',
}: DisclosureTextButtonProps) => {
  const theme = useTheme()
  const inkRest = tone === 'inverse' ? 'rgba(255,255,255,0.72)' : theme.palette.text.secondary
  const inkActive = tone === 'inverse' ? '#FFFFFF' : theme.palette.text.primary

  const shownLabel =
    typeof label === 'string' ? label : expanded ? label.expanded : label.collapsed
  // De-duped, so a pair whose two strings happen to be identical reserves one cell rather
  // than stacking a copy on top of itself.
  const reservedLabels =
    typeof label === 'string'
      ? [label]
      : [...new Set([label.expanded, label.collapsed])]

  return (
    <Stack
      component="button"
      type="button"
      direction="row"
      spacing={1}
      alignItems="center"
      onClick={onToggle}
      aria-expanded={expanded}
      aria-controls={controls}
      sx={{
        // The UA button chassis, removed. Everything visual below is this component's own.
        appearance: 'none',
        background: 'none',
        border: 'none',
        p: 0,
        font: 'inherit',
        textAlign: 'left',
        width: 'fit-content',
        cursor: 'pointer',
        color: inkRest,
        '&:hover': { color: inkActive },
        // Focus is the one state a borderless control cannot express through ink alone at
        // an accessible contrast, so it keeps a real ring. `focus-visible` rather than
        // `focus`, so a mouse click does not leave one behind.
        '&:focus-visible': {
          color: inkActive,
          outline: `2px solid ${theme.palette.brand[500]}`,
          outlineOffset: 2,
          borderRadius: 1,
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          transform: expanded ? 'rotate(0deg)' : 'rotate(-90deg)',
          transition: `transform ${MOTION_MS}ms ${EASING}`,
          '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
        }}
      >
        <ChevronDownIcon size={16} strokeWidth={2} />
      </Box>
      {/*
        Every label stacked into ONE grid cell, so the column sizes to the widest and this
        button's width stops tracking its own state. The inactive copies take `visibility:
        hidden` rather than `display: none` (which contributes no width, defeating the whole
        point) or `opacity: 0` (which would leave them laid out AND readable).

        `aria-hidden` on the inactive copies is deliberately REDUNDANT with that. The
        accessible-name computation already skips `visibility: hidden` subtrees, so the
        button's name is the visible label either way — but this button's name is now
        assembled from more DOM than it renders, and `textContent` really does read
        "Collapse chartExpand chart". Stating the exclusion outright means the name cannot
        come out as both labels concatenated under any tool that walks text rather than
        computing the name properly, which is a bad enough failure ("Collapse chartExpand
        chart") to be worth one attribute to foreclose.

        `justifyItems: start` keeps the shown label flush to the leading edge, which is what
        puts the reserved slack behind the text rather than in front of the chevron.
      */}
      <Box sx={{ display: 'grid', justifyItems: 'start', alignItems: 'center' }}>
        {reservedLabels.map(text => {
          const isShown = text === shownLabel
          return (
            <Typography
              key={text}
              variant="body3"
              color="inherit"
              aria-hidden={isShown ? undefined : true}
              sx={{
                gridArea: '1 / 1',
                whiteSpace: 'nowrap',
                visibility: isShown ? 'visible' : 'hidden',
              }}
            >
              {text}
            </Typography>
          )
        })}
      </Box>
    </Stack>
  )
}
